<template>
  <div class="chart-container">
    <h3 v-if="chartData.datasets[0].data.length === 0">No matching results</h3>
    <PieChart
      :chart-data="chartData"
      :options="options"
      style="width: 100%; height: 100%"
    />
  </div>
</template>

<script>
import { WidgetOptionItemType } from "@datadog/ui-extensions-sdk";
import PieChart from "@/components/PieChart.vue";
import { debounce, min, max } from "underscore";

export default {
  components: {
    PieChart,
  },
  data() {
    return {
      chartData: {
        labels: [],
        datasets: [{ data: [] }],
      },
      options: {
        responseive: true,
        maintainAspectRatio: false,
        resizeDelay: 0,
        legend: {
          labels: {
            fontFamily: "Noto Sans",
          },
          display: false,
        },
      },
      forms: {
        activeMetric: null,
        activeHost: null,
        activeOperation: null,
        activeGroup: null,
        reduceOperation: null,
        showLegend: null,
        chartColor: null,
      },
      context: {
        dashboard: null,
      },
      query: null,
      metricsList: [],
      colorTheme: "light",
      timer: null,
    };
  },
  mounted: function () {
    let vm = this;
    let client = vm.$client;
    // this.query = "sum:aws.ec2.host_ok{*} by {instance-type}";
    // Initialize the widget menu (this could also be done in the manifest)
    client.getContext().then((c) => {
      if (c.app.debug) console.log("context: " + JSON.stringify(c));
      if (c.dashboard !== undefined) {
        vm.context = c; // Store context locally
        // Update color theme
        vm.colorTheme = vm.context.app.currentUser.colorTheme;
        document.body.style.backgroundColor =
          this.getColorTheme() == "light" ? "#FFFFFF" : "#222126";
        // Initialize timeframe
        let start = c.dashboard?.timeframe?.start / 1000;
        let end = c.dashboard?.timeframe?.end / 1000;
        if (vm.context.app.debug)
          console.log("Querying API from: " + start.toFixed());
        if (vm.metricsList.length == 0) {
          if (vm.context.app.debug) console.log("Refreshing metrics list...");
          client.api
            .get("/api/v1/metrics", {
              params: { from: start.toFixed() },
            })
            .then((d) => {
              if (d["metrics"].length > vm.metricsList.length) {
                vm.metricsList = d["metrics"];
              } else {
                vm.metricsList = ["system.cpu.user", "aws.route53.dnsqueries"];
              }
              vm.updateWidgetOptions(client, vm.metricsList);
            })
            .catch((e) => {
              console.error(e);
            });
        }
        // On init, widget definition may be missing
        if (c.widget.definition.options !== undefined) {
          let widgetOptions = c.widget.definition.options;
          let activeMetric = widgetOptions["active-metrics"] ?? null;
          let activeHost = widgetOptions["active-host"] ?? null;
          let activeOperation = widgetOptions["active-operation"] ?? null;
          let activeGroup = widgetOptions["active-group"] ?? null;
          let reduceOperation = widgetOptions["reduce-operation"] ?? null;
          let showLegend = widgetOptions["show-legend"] ?? null;
          let chartColor = widgetOptions["chart-color"] ?? null;
          // Store options locally
          vm.forms.activeMetric = activeMetric;
          vm.forms.activeHost = activeHost;
          vm.forms.activeOperation = activeOperation;
          vm.forms.activeGroup = activeGroup;
          vm.forms.reduceOperation = reduceOperation;
          vm.forms.showLegend = showLegend;
          vm.forms.chartColor = chartColor;
          // Only perform the query when all required options filled
          if (activeMetric && activeHost && activeGroup && activeOperation) {
            createQuery(activeMetric, activeHost, activeGroup, activeOperation);
            clearInterval(vm.timer); // Reset any timers
            if (vm.context.dashboard.timeframe.isLive) {
              // Live mode refreshes every 30 seconds
              let temp_end = end;
              let temp_start = start;
              vm.timer = setInterval(() => {
                temp_end += 30;
                temp_start += 30;
                vm.updateChartData(client, temp_start, temp_end, vm.query);
              }, 30000);
            } else {
              vm.updateChartData(client, start, end, vm.query);
            }
          }
        }
      }
    });

    client.events.on("dashboard_timeframe_change", (change) => {
      if (vm.context.app.debug)
        console.log("timeframe event received:" + JSON.stringify(change));
      vm.updateContextTimeframe(change);
      let start = change["start"] / 1000;
      let end = change["end"] / 1000;
      clearInterval(vm.timer);
      vm.updateChartData(client, start, end, vm.query);
      if (change.isLive) {
        let temp_end = end;
        let temp_start = start;
        vm.timer = setInterval(() => {
          temp_end += 30;
          temp_start += 30;
          vm.updateChartData(client, temp_start, temp_end, vm.query);
        }, 30000);
      } else {
        vm.updateChartData(client, start, end, vm.query);
      }
    });

    function createQuery(metric, host, group, operation) {
      if (metric && host && operation) {
        let query = `${operation}:${metric}{${host}} by {${group}}`;
        if (vm.context.app.debug) console.log("Query: " + query);
        vm.query = query;
      }
    }

    // This must be debounced, otherwise everey letter press is an API call
    client.events.on(
      "dashboard_custom_widget_options_change",
      debounce((change) => {
        if (vm.context.app.debug)
          console.log(
            "widget options event received:" + JSON.stringify(change)
          );
        vm.updateContextWidget(change);
        let activeMetric = change["active-metrics"] ?? null;
        let activeHost = change["active-host"] ?? null;
        let activeOperation = change["active-operation"] ?? null;
        let activeGroup = change["active-group"] ?? null;
        let showLegend = change["show-legend"] ?? null;
        let chartColor = change["chart-color"] ?? null;
        vm.forms.showLegend = showLegend;
        vm.forms.chartColor = chartColor;

        // Dynamically show legend
        this.options.legend.display = this.forms.showLegend;

        if (activeMetric && activeHost && activeGroup && activeOperation) {
          createQuery(activeMetric, activeHost, activeGroup, activeOperation);
          client
            .getContext()
            .then((c) => {
              if (c.dashboard !== undefined) {
                // No need to do timer stuff here as it is only for preview
                vm.context = c;
                vm.updateChartData(
                  client,
                  c.dashboard?.timeframe?.start / 1000,
                  c.dashboard?.timeframe?.end / 1000,
                  vm.query
                );
              }
            })
            .catch((e) => {
              console.error(e);
            });
        }
      }, 1000)
    );

    client.events.on("dashboard_template_var_change", (change) => {
      if (vm.context.app.debug)
        console.log("templatevar event received:" + JSON.stringify(change));
      // vm.updateContextTemplateVars(change);
    });

    // client.events.on("dashboard_cursor_change", (change) => {
    //   if (vm.context.app.debug) console.log("event received:" + JSON.stringify(change));
    // });
  },
  methods: {
    getColorTheme() {
      return this.colorTheme;
    },
    updateContextTimeframe(change) {
      this.timeframe = { start: change.start, end: change.end };
      this.$set(this.context.dashboard, "timeframe", change);
    },
    updateContextWidget(change) {
      // console.log("Change received: " + JSON.stringify(change));
      this.$set(this.context.widget.definition, "options", change);
    },
    updateContextTemplateVars(change) {
      this.$set(this.context.dashboard, "templateVars", change);
    },
    updateWidgetOptions(client, metricsList) {
      client.dashboard.customWidget.updateOptions([
        {
          type: WidgetOptionItemType.STRING,
          name: "active-metrics",
          label: "Metric",
          enum: metricsList ? (metricsList.length ? metricsList : "") : "",
          default: this.forms.activeMetric ?? "system.cpu.user",
          loading: metricsList ? false : true,
          required: true,
          order: 1,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-host",
          label: "From",
          default: this.forms.activeHost ?? "*",
          required: true,
          order: 2,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-operation",
          label: "Operation",
          enum: ["avg", "max", "min", "sum"],
          default: this.forms.activeOperation ?? "avg",
          required: true,
          order: 3,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "active-group",
          label: "Group by",
          default: this.forms.activeGroup ?? "instance-type",
          required: true,
          order: 4,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "reduce-operation",
          label: "Reduce by",
          enum: ["avg", "last", "max", "min", "sum"],
          default: this.forms.reduceOperation ?? "avg",
          required: true,
          order: 5,
        },
        {
          type: WidgetOptionItemType.BOOLEAN,
          name: "show-legend",
          label: "Show Legend",
          default: this.forms.showLegend ?? false,
          order: 6,
        },
        {
          type: WidgetOptionItemType.STRING,
          name: "chart-color",
          label: "Color",
          enum: ["default", "cool", "warm", "purple", "orange", "gray"],
          default: this.forms.chartColor ?? "default",
          order: 7,
        },
      ]);
    },
    getChartColor(theme) {
      let colors;
      switch (theme) {
        case "cool": {
          colors = [
            "#0868AC80",
            "#2B8CBE80",
            "#4EB3D380",
            "#7BCCC480",
            "#A8DDB580",
            "#CCEBC580",
            "#E0F3DB80",
          ];
          break;
        }
        case "warm": {
          colors = [
            "#BD002680",
            "#E31A1C80",
            "#FC4E2A80",
            "#FD8D3C80",
            "#FEB24C80",
            "#FED97680",
            "#FFEDA080",
          ];
          break;
        }
        case "purple": {
          colors = [
            "#44269980",
            "#5F35BF80",
            "#784BDF80",
            "#8D63F280",
            "#A884FA80",
            "#C9B3FF80",
            "#EEE5FF80",
          ];
          break;
        }
        case "orange": {
          colors = [
            "#A6360380",
            "#D9480180",
            "#F1691380",
            "#FD8D3C80",
            "#FDAE6B80",
            "#FDD0A280",
            "#FEE6CE80",
          ];
          break;
        }
        case "gray": {
          colors = [
            "#8D8D9A80",
            "#ACB0B980",
            "#C5CBD180",
            "#4C4C5780",
            "#63636F80",
            "#7A7C8680",
            "#92969E80",
          ];
          break;
        }
        default: {
          colors = [
            "#7463E180",
            "#D7C3FF80",
            "#3598EC80",
            "#FFEF9580",
            "#FFCD1E80",
          ];
          break;
        }
      }
      return colors;
    },
    updateChartData(client, start, end, query) {
      client.api
        .get("/api/v1/query", {
          params: { from: start, to: end, query: query },
        })
        .then((d) => {
          let series = d.series;
          let newArray = [];
          let newLabels = [];
          let newDict = [];
          let reduce =
            this.context.widget.definition.options["reduce-operation"];
          // console.log("Reduce op: " + JSON.stringify(reduce));
          for (let i = 0; i < series.length; i++) {
            let key = series[i].scope;
            let pointlist = series && series[i].pointlist;
            let valueList = pointlist && pointlist.map((x) => x[1]);
            let value;
            switch (reduce) {
              case "avg": {
                value =
                  valueList.reduce(function (sum, val) {
                    return sum + val;
                  }, 0) / valueList.length;
                value = value.toFixed(2);
                break;
              }
              case "last": {
                value = valueList[valueList.length - 1];
                break;
              }
              case "max": {
                value = max(valueList);
                break;
              }
              case "min": {
                value = min(valueList);
                break;
              }
              case "sum": {
                value = valueList.reduce((a, b) => a + b, 0);
                break;
              }
              default: {
                break;
              }
            }
            newDict.push({ label: key, value: value });
          }
          newDict.sort(function (a, b) {
            return a.value < b.value ? -1 : a.value == b.value ? 0 : 1;
          });
          for (var k = 0; k < newDict.length; k++) {
            newLabels.push(newDict[k].label);
            newArray.push(newDict[k].value);
          }
          let arr = this.getChartColor(this.forms.chartColor);
          let colorArray = [];
          for (var i = 0, n = arr.length; i < newLabels.length; i++) {
            colorArray.push(arr[((i % n) + n) % n]);
          }
          this.chartData = {
            labels: newLabels,
            datasets: [
              {
                label: "Data One",
                backgroundColor:
                  this.context.widget.definition.custom_widget_key ==
                  "rapdev_radar"
                    ? colorArray[0]
                    : colorArray,
                borderColor:
                  this.colorTheme == "light" ? "#FFFFFF79" : "#22212680",
                fill: true,
                data: newArray,
              },
            ],
          };
          this.options.scale = {
            gridLines: {
              color: this.colorTheme == "light" ? "#E5E5E5" : "#3C3C3C",
            },
            angleLines: {
              color: this.colorTheme == "light" ? "#E5E5E5" : "#3C3C3C",
            },
            ticks: {
              backdropColor: "transparent",
              // eslint-disable-next-line
              callback: function (value, index, values) {
                // This is a formatting function used by chartjs to display the number
                if (value >= 1000) {
                  var units = ["k", "M", "B", "T"];
                  var order = Math.floor(Math.log(value) / Math.log(1000));
                  var unitname = units[order - 1];
                  var num = Math.floor(value / 1000 ** order);
                  return num + unitname;
                }
                return value;
              },
            },
          };
        })
        .catch((e) => {
          console.error(e);
        });
    },
  },
};
</script>
<style>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;700&display=swap");
.chart-container {
  overflow: hidden;
  position: relative;
  padding: 0px;
  height: 94vh;
  width: 97vw;
}
</style>